/**
 * Parses `ProductSwatchesPricingOptionViewModel.Json` — the CMS half of the
 * configurator. Availability and pricing come from the API and are joined on
 * `attributeCode`, never on the display label, which is translatable.
 */

export interface ConfiguratorOptionValue {
  label: string;
  valueIndex: number;
  uid: string;
}

export interface ConfiguratorGroup {
  attributeCode: string;
  title: string;
  description?: string;
  values: ConfiguratorOptionValue[];
  informationText?: string;
  informationTooltipText?: string;
}

export interface ConfiguratorPage {
  title: string;
  descriptionHtml?: string;
  groups: ConfiguratorGroup[];
}

export interface ConfiguratorConfig {
  pages: ConfiguratorPage[];
  lastGroupTitle?: string;
  lastGroupDescription?: string;
  lastGroupText?: string;
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reads a key in either PascalCase or camelCase. */
function read(source: UnknownRecord, pascalKey: string): unknown {
  if (pascalKey in source) {
    return source[pascalKey];
  }

  const camelKey = pascalKey.charAt(0).toLowerCase() + pascalKey.slice(1);
  return source[camelKey];
}

function readString(source: UnknownRecord, pascalKey: string): string | undefined {
  const value = read(source, pascalKey);
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function readArray(source: UnknownRecord, pascalKey: string): UnknownRecord[] {
  const value = read(source, pascalKey);
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function parseValue(source: UnknownRecord): ConfiguratorOptionValue | null {
  const uid = readString(source, 'Uid');
  const label = readString(source, 'Label');
  const valueIndex = read(source, 'ValueIndex');

  if (!uid || typeof valueIndex !== 'number' || !Number.isFinite(valueIndex)) {
    return null;
  }

  return { uid, valueIndex, label: label ?? String(valueIndex) };
}

function parseGroup(source: UnknownRecord): ConfiguratorGroup | null {
  const attributeCode = readString(source, 'ConfigurableOption');
  const title = readString(source, 'GroupTitle');

  if (!attributeCode) {
    return null;
  }

  const values = readArray(source, 'ConfigurableOptionValues')
    .map(parseValue)
    .filter((value): value is ConfiguratorOptionValue => value !== null);

  return {
    attributeCode,
    title: title ?? attributeCode,
    description: readString(source, 'GroupDescription'),
    values,
    informationText: readString(source, 'GroupInformationText'),
    informationTooltipText: readString(source, 'GroupInformationTooltipText')
  };
}

function parsePage(source: UnknownRecord): ConfiguratorPage | null {
  const groups = readArray(source, 'Groups')
    .map(parseGroup)
    .filter((group): group is ConfiguratorGroup => group !== null);

  if (groups.length === 0) {
    return null;
  }

  return {
    title: readString(source, 'PageTitle') ?? '',
    descriptionHtml: readString(source, 'PageDescriptionHtml'),
    groups
  };
}

/** Null when the payload is missing, malformed, or has no usable page. */
export function parseConfiguratorConfig(
  json: string | null | undefined
): ConfiguratorConfig | null {
  if (!json?.trim()) {
    return null;
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }

  if (!isRecord(parsed)) {
    return null;
  }

  const pages = readArray(parsed, 'Pages')
    .map(parsePage)
    .filter((page): page is ConfiguratorPage => page !== null);

  if (pages.length === 0) {
    return null;
  }

  return {
    pages,
    lastGroupTitle: readString(parsed, 'LastGroupTitle'),
    lastGroupDescription: readString(parsed, 'LastGroupDescription'),
    lastGroupText: readString(parsed, 'LastGroupText')
  };
}

export function collectAttributeCodes(config: ConfiguratorConfig): string[] {
  return config.pages.flatMap(page => page.groups.map(group => group.attributeCode));
}
